import { RecordCollection } from '../../app/record-collection.js';
import { TransferSuggestion } from './transfers.js';
import {
  Button,
  Card,
  DateRangePicker,
  Input,
  Modal,
  StatusPill,
  formatCivilDate,
  formatCivilRange,
  formatCurrency,
  type CivilDate,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Handshake,
  Link2Off,
  Scale,
  ShieldQuestion,
  Sparkles,
  Undo2,
  RotateCcw,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  getApiError,
  reconciliationApi,
  reconciliationRulesApi,
  type Currency,
  type FinanceAccount,
  type FinanceDifferenceReason,
  type ReconciliationEntry,
  type ReconciliationMovement,
  type ReconciliationObligation,
  type ReconciliationState,
  type ReconciliationRuleProposal,
  type SettlementTarget,
} from '../../lib/api.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  MultiFilter,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import { choiceChips } from '../../app/choice-filters.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import { financeViewDefaults, type FinanceView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';

export interface ReconciliationCapabilities {
  canSettle: boolean;
  canManageRules: boolean;
}

const stateOptions: Array<{ value: ReconciliationState; label: string }> = [
  { value: 'UNLINKED', label: 'Sem vínculo' },
  { value: 'SETTLED', label: 'Conciliados' },
  { value: 'IGNORED', label: 'Sem compromisso' },
];

/// Sem vínculo e Conciliados juntos é o padrão de sempre, "A conciliar": tudo que não foi
/// declarado sem compromisso.
function stateSummary(chosen: readonly ReconciliationState[]): string | undefined {
  if (chosen.length === 0 || chosen.length === 3) return 'Todos';
  if (chosen.length === 2 && !chosen.includes('IGNORED')) return 'A conciliar';
  return undefined;
}

/// A conciliação vista pelo extrato: cada pagamento com o que ele quitou, o que ainda sobra dele e
/// a proposta de conjunto quando o valor fecha com compromissos da mesma contraparte (ADR 0021).
export function ReconciliationPanel({
  accounts,
  capabilities,
  today,
  view,
  onView,
}: {
  accounts: FinanceAccount[];
  capabilities: ReconciliationCapabilities;
  today: CivilDate;
  view: FinanceView;
  onView: ViewSetter<FinanceView>;
}) {
  const period = useOperationalPeriod(today);
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [settling, setSettling] = useState<ReconciliationEntry>();
  const [proposal, setProposal] = useState<ReconciliationRuleProposal>();
  const [dismissing, setDismissing] = useState<ReconciliationEntry>();
  const [resolving, setResolving] = useState<ReconciliationEntry>();

  /// O recorte de dias manda no mês de trabalho. Quem aponta dois dias na grade quer esses dois
  /// dias, mesmo que caiam fora do mês da barra — e "a partir de tal dia" não ganha um fim que
  /// ninguém pediu. Sem recorte próprio, o mês continua valendo, que é o estado de sempre.
  const range = { from: view.from ?? null, to: view.to ?? null };
  const ranged = Boolean(view.from ?? view.to);
  const from = ranged ? view.from : period.from;
  const to = ranged ? view.to : period.to;

  const filters = {
    ...(view.reconciliationAccount.length ? { financeAccountId: view.reconciliationAccount } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(view.state.length ? { state: view.state } : {}),
    flow: 'ALL' as const,
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'reconciliation', filters],
    queryFn: () => reconciliationApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });

  async function refreshed(message: string) {
    await queryClient.invalidateQueries({ queryKey: ['company', 'reconciliation'] });
    await queryClient.invalidateQueries({ queryKey: ['company', 'financial-obligations'] });
    await queryClient.invalidateQueries({ queryKey: ['company', 'finance', 'movements'] });
    // Tratar um movimento muda a fila do fechamento: a aba ao lado não pode continuar mostrando
    // como bloqueio algo que acabou de ser resolvido.
    await queryClient.invalidateQueries({ queryKey: ['company', 'finance', 'closing'] });
    setSettling(undefined);
    setDismissing(undefined);
    setResolving(undefined);
    snackbar.success(message);
  }

  async function undo(settlementId: string) {
    try {
      await reconciliationApi.undo(settlementId);
      await refreshed('Conciliação desfeita.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    }
  }

  async function resume(movementId: string) {
    try {
      await reconciliationApi.resume(movementId);
      await refreshed('Movimento de volta na fila.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    }
  }

  /// Desfazer devolve a sobra ao estado de não explicada — e o mês volta a travar por causa dela.
  async function undoDifference(movementId: string) {
    try {
      await reconciliationApi.undoDifference(movementId);
      await refreshed('Diferença desfeita. A sobra voltou a pendente.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    }
  }

  const entries = page.data?.entries ?? [];
  const total = page.data?.total ?? 0;
  const accountOptions = accounts.map((item) => ({ value: item.id, label: item.displayName }));
  const defaultState = stateSummary(view.state) === 'A conciliar';
  const chips = [
    ...(defaultState
      ? []
      : [
          {
            key: 'state',
            label:
              stateSummary(view.state) === 'Todos'
                ? 'Todas as situações'
                : stateOptions
                    .filter((one) => view.state.includes(one.value))
                    .map((one) => one.label)
                    .join(', '),
            onRemove: () => onView({ state: financeViewDefaults.state, offset: 0 }),
          },
        ]),
    ...choiceChips('account', view.reconciliationAccount, accountOptions, (reconciliationAccount) =>
      onView({ reconciliationAccount, offset: 0 }),
    ),
    ...(ranged
      ? [
          {
            key: 'range',
            label: formatCivilRange(range, 'Mês de trabalho'),
            onRemove: () => onView({ from: undefined, to: undefined, offset: 0 }),
          },
        ]
      : []),
  ];

  function movement(entry: ReconciliationEntry, presentation: 'card' | 'details' | 'actions') {
    return (
      <MovementCard
        presentation={presentation}
        entry={entry}
        candidates={page.data?.candidates ?? []}
        capabilities={capabilities}
        onSettle={() => setSettling(entry)}
        onDismiss={() => setDismissing(entry)}
        onResume={() => void resume(entry.movement.id)}
        onUndo={(settlementId) => void undo(settlementId)}
        onResolve={() => setResolving(entry)}
        onUndoDifference={() => void undoDifference(entry.movement.id)}
      />
    );
  }
  return (
    <section aria-labelledby="reconciliation-title">
      <h2 id="reconciliation-title" className="sr-only">
        Conciliação
      </h2>
      <FilterBar
        searchId="reconciliation-search"
        searchLabel="Buscar no extrato"
        searchPlaceholder="Descrição do movimento"
        search={view.search}
        onSearchChange={(value) => onView({ search: value, offset: 0 })}
        chips={chips}
        onClear={() =>
          onView({
            search: '',
            state: financeViewDefaults.state,
            reconciliationAccount: [],
            from: undefined,
            to: undefined,
            offset: 0,
          })
        }
        count={`${String(total)} ${total === 1 ? 'movimento' : 'movimentos'}`}
        resultsLabel={`Ver ${String(total)} ${total === 1 ? 'movimento' : 'movimentos'}`}
        pageSize={view.limit}
        onPageSizeChange={(size) => onView({ limit: size, offset: 0 })}
      >
        <MultiFilter
          label="Situação"
          itemNoun="situações"
          allLabel="Todos"
          summary={stateSummary(view.state)}
          options={stateOptions}
          value={view.state}
          onChange={(state) => onView({ state, offset: 0 })}
        />
        <MultiFilter
          label="Conta financeira"
          itemNoun="contas financeiras"
          allLabel="Todas as contas financeiras"
          options={accountOptions}
          value={view.reconciliationAccount}
          onChange={(reconciliationAccount) => onView({ reconciliationAccount, offset: 0 })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Dias</span>
          <DateRangePicker
            label="Período do extrato"
            value={range}
            onValueChange={(next) =>
              onView({ from: next.from ?? undefined, to: next.to ?? undefined, offset: 0 })
            }
            today={today}
            emptyLabel="Mês de trabalho"
          />
        </div>
      </FilterBar>

      {page.data && page.data.pendingCents > 0 ? (
        <p className="mb-4 text-sm text-content-muted">
          Falta conciliar{' '}
          <strong className="text-content">{formatCurrency(page.data.pendingCents)}</strong> no
          período.
        </p>
      ) : null}

      {page.isLoading ? <LoadingCard label="Carregando o extrato…" /> : null}
      {page.error ? <ErrorCard message={getApiError(page.error)} /> : null}
      {!page.isLoading && !page.error && entries.length === 0 ? (
        <EmptyCard
          icon={Handshake}
          title="Nada para conciliar aqui"
          description="Escolha outros dias, outra conta ou mude a situação para ver o que já foi conciliado."
        />
      ) : null}

      <RecordCollection
        label="Movimentos para conciliação"
        records={entries}
        recordKey={(entry) => entry.movement.id}
        sorting={{
          sort: view.sort,
          direction: view.direction,
          columns: ['occurredOn', 'amount'],
          onToggle: (key) => {
            if (key === 'occurredOn' || key === 'amount')
              onView({
                sort: key,
                direction: view.sort === key && view.direction === 'asc' ? 'desc' : 'asc',
                offset: 0,
              });
          },
        }}
        columns={[
          {
            key: 'occurredOn',
            label: 'Data',
            render: (entry) => (
              <span className="text-xs text-content-muted">
                {formatCivilDate(entry.movement.occurredOn)}
              </span>
            ),
          },
          {
            key: 'description',
            label: 'Movimento',
            render: (entry) => (
              <div>
                <strong>{entry.movement.description}</strong>
                {entry.movement.ignoredAt === null &&
                entry.movement.settledCents === 0 &&
                !entry.movement.difference ? (
                  <TransferSuggestion
                    movementId={entry.movement.id}
                    canSettle={capabilities.canSettle}
                  />
                ) : null}
              </div>
            ),
          },
          {
            key: 'account',
            label: 'Conta financeira',
            render: (entry) => entry.movement.accountName,
          },
          {
            key: 'direction',
            label: 'Direção',
            render: (entry) => (
              <StatusPill tone={entry.movement.direction === 'OUTFLOW' ? 'warning' : 'positive'}>
                {entry.movement.direction === 'OUTFLOW' ? 'Saída' : 'Entrada'}
              </StatusPill>
            ),
          },
          { key: 'status', label: 'Situação', render: (entry) => <MovementStatus entry={entry} /> },
          {
            key: 'amount',
            label: 'Valor',
            render: (entry) => (
              <div>
                <strong className="whitespace-nowrap tabular-nums">
                  {formatCurrency(entry.movement.amountCents, entry.movement.currency)}
                </strong>
                {entry.movement.settledCents > 0 ? (
                  <p className="text-xs text-content-muted">
                    Livre: {formatCurrency(availableOf(entry), entry.movement.currency)}
                  </p>
                ) : null}
              </div>
            ),
          },
        ]}
        actions={(entry) => movement(entry, 'actions')}
        details={(entry) => movement(entry, 'details')}
        card={(entry) => movement(entry, 'card')}
      />

      {total > 0 ? (
        <Pagination
          total={total}
          limit={view.limit}
          offset={view.offset}
          onOffsetChange={(offset) => onView({ offset })}
          itemLabel={(count) => (count === 1 ? 'movimento' : 'movimentos')}
        />
      ) : null}

      {settling ? (
        <SettleMovementModal
          entry={settling}
          candidates={page.data?.candidates ?? []}
          onClose={() => setSettling(undefined)}
          onSettled={refreshed}
          onProposal={capabilities.canManageRules ? setProposal : undefined}
        />
      ) : null}
      {proposal ? (
        <RuleProposalModal
          proposal={proposal}
          onClose={() => setProposal(undefined)}
          onCreated={async () => {
            setProposal(undefined);
            await queryClient.invalidateQueries({ queryKey: ['company', 'reconciliation-rules'] });
            snackbar.success('Regra criada. Ajuste a ordem em Financeiro → Regras.');
          }}
        />
      ) : null}
      {dismissing ? (
        <DismissModal
          entry={dismissing}
          onClose={() => setDismissing(undefined)}
          onDismissed={refreshed}
        />
      ) : null}
      {resolving ? (
        <ResolveDifferenceModal
          entry={resolving}
          onClose={() => setResolving(undefined)}
          onResolved={refreshed}
        />
      ) : null}
    </section>
  );
}

/// Quanto deste pagamento ainda não foi usado. É o que ele consegue quitar, aqui ou em qualquer
/// outro compromisso.
function freeOf(movement: ReconciliationMovement): number {
  return Math.max(movement.amountCents - movement.settledCents, 0);
}

function availableOf(entry: ReconciliationEntry): number {
  return freeOf(entry.movement);
}

function MovementStatus({ entry }: { entry: ReconciliationEntry }) {
  const available = availableOf(entry);
  return entry.movement.ignoredAt !== null ? (
    <StatusPill tone="neutral">Sem compromisso</StatusPill>
  ) : entry.movement.difference ? (
    <StatusPill tone="positive">Diferença resolvida</StatusPill>
  ) : available === 0 ? (
    <StatusPill tone="positive">Conciliado</StatusPill>
  ) : entry.movement.settledCents > 0 ? (
    <StatusPill tone="warning">Conciliado em parte</StatusPill>
  ) : (
    <StatusPill tone="neutral">A conciliar</StatusPill>
  );
}

function MovementCard({
  presentation = 'card',
  entry,
  candidates,
  capabilities,
  onSettle,
  onDismiss,
  onResume,
  onUndo,
  onResolve,
  onUndoDifference,
}: {
  presentation?: 'card' | 'details' | 'actions';
  entry: ReconciliationEntry;
  candidates: ReconciliationObligation[];
  capabilities: ReconciliationCapabilities;
  onSettle: () => void;
  onDismiss: () => void;
  onResume: () => void;
  onUndo: (settlementId: string) => void;
  onResolve: () => void;
  onUndoDifference: () => void;
}) {
  const available = availableOf(entry);
  const ignored = entry.movement.ignoredAt !== null;
  const resolved = entry.movement.difference;
  const byKey = new Map(candidates.map((item) => [item.key, item]));

  const actions = capabilities.canSettle ? (
    <div
      className={
        presentation === 'actions'
          ? 'flex flex-wrap justify-end gap-1'
          : 'mt-3 flex flex-wrap gap-1'
      }
    >
      {ignored ? (
        <IconButton label="Voltar para a fila" icon={<RotateCcw />} onClick={onResume} />
      ) : resolved ? (
        <IconButton label="Desfazer diferença" icon={<Undo2 />} onClick={onUndoDifference} />
      ) : (
        <>
          <IconButton
            label="Conciliar"
            icon={<Handshake />}
            onClick={onSettle}
            disabled={available === 0}
          />
          <IconButton
            label="Sem compromisso"
            icon={<ShieldQuestion />}
            onClick={onDismiss}
            disabled={entry.links.length > 0}
          />
          {/* Sem vínculo, o que está em questão é a linha inteira, e para isso existe "Sem
                  compromisso". Resolver explica a sobra de um pagamento que quitou algo (ADR 0026). */}
          {available > 0 && entry.links.length > 0 ? (
            <IconButton label="Resolver diferença" icon={<Scale />} onClick={onResolve} />
          ) : null}
        </>
      )}
    </div>
  ) : null;
  if (presentation === 'actions') return actions;
  if (
    presentation === 'details' &&
    !entry.suggestion &&
    !entry.movement.ignoredReason &&
    !resolved &&
    entry.links.length === 0
  )
    return null;
  return (
    <Card
      className={
        presentation === 'details'
          ? 'border-0 bg-transparent !p-0 shadow-none'
          : 'bg-surface-card p-4 sm:p-5'
      }
    >
      {presentation === 'card' ? (
        <>
          {!ignored && entry.movement.settledCents === 0 && !resolved ? (
            <TransferSuggestion movementId={entry.movement.id} canSettle={capabilities.canSettle} />
          ) : null}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-content-muted">
                {formatCivilDate(entry.movement.occurredOn)} · {entry.movement.accountName}
              </p>
              <p className="mt-1 break-words font-semibold text-content">
                {entry.movement.description}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <StatusPill tone={entry.movement.direction === 'OUTFLOW' ? 'warning' : 'positive'}>
                  {entry.movement.direction === 'OUTFLOW' ? 'Saída' : 'Entrada'}
                </StatusPill>
                {ignored ? (
                  <StatusPill tone="neutral">Sem compromisso</StatusPill>
                ) : resolved ? (
                  <StatusPill tone="positive">Diferença resolvida</StatusPill>
                ) : available === 0 ? (
                  <StatusPill tone="positive">Conciliado</StatusPill>
                ) : entry.movement.settledCents > 0 ? (
                  <StatusPill tone="warning">Conciliado em parte</StatusPill>
                ) : (
                  <StatusPill tone="neutral">A conciliar</StatusPill>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold tabular-nums">
                {formatCurrency(entry.movement.amountCents, entry.movement.currency)}
              </p>
              {available > 0 && entry.movement.settledCents > 0 ? (
                <p className="text-xs text-content-muted">
                  Livre: {formatCurrency(available, entry.movement.currency)}
                </p>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
      {ignored && entry.movement.ignoredReason ? (
        <p className="mt-3 rounded-xl bg-surface-elevated px-3 py-2 text-sm text-content-muted">
          {entry.movement.ignoredReason}
        </p>
      ) : null}

      {resolved ? (
        <p className="mt-3 rounded-xl bg-surface-elevated px-3 py-2 text-sm text-content-muted">
          Sobra de {formatCurrency(resolved.amountCents, entry.movement.currency)} resolvida como{' '}
          {reasonLabelOf(resolved.reason).toLowerCase()}
          {resolved.note ? `: ${resolved.note}` : '.'}
        </p>
      ) : null}

      {entry.suggestion ? (
        <div className="mt-3 rounded-xl border border-brand-500/30 bg-brand-500/10 px-3 py-2.5">
          <p className="flex items-center gap-2 text-sm font-semibold text-brand-300">
            <Sparkles className="size-4 shrink-0" aria-hidden="true" />
            {entry.suggestion.confidence === 'HIGH' ? 'Proposta' : 'Proposta a conferir'}
          </p>
          <p className="mt-1 text-sm text-content-muted">{entry.suggestion.reason}</p>
          <ul className="mt-2 space-y-1">
            {entry.suggestion.keys.map((key) => (
              <li key={key} className="text-sm text-content">
                {byKey.get(key)?.counterpartyName ?? 'Compromisso'} ·{' '}
                {byKey.get(key)?.description ?? ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {entry.links.length > 0 ? (
        <ul className="mt-3 divide-y divide-surface-border border-t border-surface-border">
          {entry.links.map((link) => (
            <li key={link.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
              <span className="min-w-0 text-sm text-content-muted">
                Quitou {formatCurrency(link.amountCents, entry.movement.currency)} em{' '}
                {formatCivilDate(link.settledOn)}
              </span>
              {capabilities.canSettle ? (
                <IconButton label="Desfazer" icon={<Link2Off />} onClick={() => onUndo(link.id)} />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {presentation === 'card' ? actions : null}
    </Card>
  );
}

/// "Sugerir pagamentos parecidos no futuro?" — a regra chega preenchida a partir do pagamento que
/// acabou de ser conciliado, e só existe depois que alguém revisa e confirma (ADR 0024).
function RuleProposalModal({
  proposal,
  onClose,
  onCreated,
}: {
  proposal: ReconciliationRuleProposal;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState(proposal.name);
  const [term, setTerm] = useState(proposal.term);
  const [failure, setFailure] = useState<string>();
  const [saving, setSaving] = useState(false);

  async function create() {
    setSaving(true);
    setFailure(undefined);
    try {
      await reconciliationRulesApi.create({
        name: name.trim(),
        active: true,
        mode: 'SIMPLE',
        operator: 'CONTAINS',
        terms: [term.trim()],
        direction: proposal.direction,
        financeAccountId: null,
        minAmountCents: null,
        maxAmountCents: null,
        target: proposal.target,
      });
      await onCreated();
    } catch (error) {
      setFailure(getApiError(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Sugerir pagamentos parecidos no futuro?"
      description={`Pagamentos cuja descrição contenha esse texto passam a chegar apontando ${proposal.targetLabel}. A sugestão nunca concilia sozinha.`}
    >
      <div className="grid gap-4">
        <Input
          label="Nome da regra"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label="Texto procurado na descrição"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
        {failure ? <RequestAlert message={failure} /> : null}
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Agora não
        </Button>
        <Button
          loading={saving}
          disabled={name.trim().length < 2 || term.trim().length < 2}
          onClick={() => void create()}
        >
          Criar regra
        </Button>
      </div>
    </Modal>
  );
}

/// Quanto desta obrigação este pagamento ainda pode quitar. A baixa manual entra na conta porque
/// conciliar substitui aquela baixa: a parcela zerada à mão volta a caber quando o pagamento de
/// verdade aparece no extrato (ADR 0025).
function effectiveOutstandingOf(obligation: ReconciliationObligation): number {
  return obligation.outstandingCents + obligation.manuallySettledCents;
}

/// A busca dentro do diálogo. Procura pelo que a pessoa tem diante dos olhos: o nome da
/// contraparte, a descrição, de onde o compromisso veio e o valor como ele aparece escrito. Digitar
/// só números encontra o valor sem depender de ponto, vírgula ou "R$".
function digitsOf(value: string): string {
  return value.replace(/\D/gu, '');
}

function matchesObligation(obligation: ReconciliationObligation, term: string): boolean {
  const amount = formatCurrency(effectiveOutstandingOf(obligation), obligation.currency);
  const text =
    `${obligation.counterpartyName} ${obligation.description} ${obligation.sourceLabel} ${amount}`.toLowerCase();
  if (text.includes(term)) return true;
  const digits = digitsOf(term);
  return digits.length > 0 && digitsOf(amount).includes(digits);
}

/// "12 de 34 compromissos · 2 escolhidos". A lista rola dentro do diálogo, então a contagem é o que
/// diz se o que se procura ficou de fora da busca ou só está mais abaixo.
function countLabel(shown: number, total: number, chosen: number, noun: string): string {
  const base =
    shown === total
      ? `${String(total)} ${total === 1 ? noun : `${noun}s`}`
      : `${String(shown)} de ${String(total)} ${noun}s`;
  return chosen === 0 ? base : `${base} · ${String(chosen)} escolhido${chosen === 1 ? '' : 's'}`;
}

/// A busca do diálogo, com a contagem do que ela deixou à vista. A contagem importa porque a lista
/// rola: sem ela não dá para saber se o que se procura ficou de fora ou só está mais abaixo.
function DialogSearch({
  label,
  placeholder,
  value,
  onChange,
  count,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  count: string;
}) {
  return (
    <div className="sticky -top-5 z-10 -mx-4 bg-surface-card px-4 pb-2 pt-1 sm:-mx-7 sm:-top-6 sm:px-7">
      <Input
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="mt-1.5 text-xs text-content-faint">{count}</p>
    </div>
  );
}

/// Escolher o que este pagamento quita. A distribuição é sempre do vencimento mais antigo para o
/// mais novo, então a tela mostra o que vai sobrar antes de confirmar.
///
/// A lista chega ordenada pelo que tem mais chance de ser a resposta — o que a proposta apontou,
/// depois o que fecha exatamente com o valor livre, depois o vencimento mais antigo — e tem busca
/// própria. Rolar uma lista de dezenas de compromissos atrás de um nome era o caminho mais longo
/// possível para a tarefa mais comum da tela.
function SettleMovementModal({
  entry,
  candidates,
  onClose,
  onSettled,
  onProposal,
}: {
  entry: ReconciliationEntry;
  candidates: ReconciliationObligation[];
  onClose: () => void;
  onSettled: (message: string) => Promise<void>;
  onProposal?: ((proposal: ReconciliationRuleProposal) => void) | undefined;
}) {
  const available = availableOf(entry);
  const usable = useMemo(() => {
    const suggested = new Set(entry.suggestion?.keys ?? []);
    const fits = (item: ReconciliationObligation) =>
      Number(effectiveOutstandingOf(item) === available);
    return (
      candidates
        .filter(
          (item) =>
            item.currency === entry.movement.currency &&
            (entry.movement.direction === 'OUTFLOW'
              ? item.direction === 'PAYABLE'
              : item.direction === 'RECEIVABLE') &&
            !entry.links.some(
              (link) =>
                link.target.sourceKey === item.target.sourceKey &&
                link.target.entity === item.target.entity &&
                link.target.externalId === item.target.externalId &&
                link.target.occurrence === item.target.occurrence,
            ),
        )
        // Primeiro o que a proposta apontou, depois o que fecha exatamente com o valor livre, depois
        // o vencimento mais antigo. A ordem não depende do que está marcado: a linha que se acabou de
        // escolher saltar para o topo desorienta mais do que ajuda.
        .sort((left, right) => {
          const bySuggestion = Number(suggested.has(right.key)) - Number(suggested.has(left.key));
          if (bySuggestion !== 0) return bySuggestion;
          const byFit = fits(right) - fits(left);
          if (byFit !== 0) return byFit;
          return left.dueOn.localeCompare(right.dueOn);
        })
    );
  }, [candidates, entry, available]);
  const [selected, setSelected] = useState<string[]>(entry.suggestion?.keys ?? []);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string>();

  const term = search.trim().toLowerCase();
  const shown = term.length === 0 ? usable : usable.filter((item) => matchesObligation(item, term));
  const chosen = usable.filter((item) => selected.includes(item.key));
  // O que foi escolhido continua valendo mesmo depois de a busca tirá-lo da vista; some da lista,
  // não da conciliação, e a tela precisa dizer isso.
  const hiddenChoices = chosen.filter((item) => !shown.includes(item)).length;
  const applied = chosen.reduce(
    (state, item) => {
      const amount = Math.min(state.rest, effectiveOutstandingOf(item));
      return { rest: state.rest - amount, used: state.used + amount };
    },
    { rest: available, used: 0 },
  );

  async function submit() {
    setSaving(true);
    setFailure(undefined);
    try {
      const result = await reconciliationApi.settleMovement(
        entry.movement.id,
        chosen.map((item): SettlementTarget => item.target),
      );
      await onSettled(
        result.residualCents > 0
          ? `Conciliado. Sobraram ${formatCurrency(result.residualCents, entry.movement.currency)} neste movimento.`
          : 'Movimento conciliado.',
      );
      // A oferta vem depois da baixa confirmada, nunca antes: primeiro o fato, depois a conveniência.
      if (result.proposal && onProposal) onProposal(result.proposal);
    } catch (error) {
      setFailure(getApiError(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Conciliar pagamento"
      description={`${formatCivilDate(entry.movement.occurredOn)} · ${entry.movement.description} · ${formatCurrency(available, entry.movement.currency)} a distribuir`}
      footer={
        <div className="flex w-full flex-col gap-3">
          <div className="rounded-xl bg-surface-elevated px-3 py-2.5 text-sm">
            <p className="flex justify-between gap-3">
              <span className="text-content-muted">Vai quitar</span>
              <strong className="tabular-nums">
                {formatCurrency(applied.used, entry.movement.currency)}
              </strong>
            </p>
            <p className="mt-1 flex justify-between gap-3">
              <span className="text-content-muted">Sobra no movimento</span>
              <strong className="tabular-nums">
                {formatCurrency(applied.rest, entry.movement.currency)}
              </strong>
            </p>
          </div>
          {failure ? <p className="text-sm font-semibold text-danger-400">{failure}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => void submit()} disabled={chosen.length === 0 || saving}>
              <Check className="size-4" aria-hidden="true" />
              {saving ? 'Conciliando…' : 'Conciliar'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {usable.length === 0 ? (
          <p className="text-sm text-content-muted">
            Nenhum compromisso em aberto combina com este movimento no período carregado.
          </p>
        ) : (
          <>
            <DialogSearch
              label="Buscar compromisso"
              placeholder="Nome, descrição ou valor"
              value={search}
              onChange={setSearch}
              count={countLabel(shown.length, usable.length, chosen.length, 'compromisso')}
            />
            {shown.length === 0 ? (
              <p className="text-sm text-content-muted">
                Nenhum compromisso encontrado nesta busca.
              </p>
            ) : (
              <ul className="space-y-2">
                {shown.map((item) => (
                  <li key={item.key}>
                    <ChoiceRow
                      checked={selected.includes(item.key)}
                      onToggle={() =>
                        setSelected((current) =>
                          current.includes(item.key)
                            ? current.filter((key) => key !== item.key)
                            : [...current, item.key],
                        )
                      }
                      title={item.counterpartyName}
                      subtitle={`${item.description} · vence ${formatCivilDate(item.dueOn)}`}
                      badge={item.sourceLabel}
                      amount={formatCurrency(effectiveOutstandingOf(item), item.currency)}
                      {...(item.manualSettlements.length > 0
                        ? {
                            note: `Baixada à mão em ${item.manualSettlements
                              .map((settlement) => formatCivilDate(settlement.settledOn))
                              .join(', ')} — conciliar substitui essa baixa.`,
                          }
                        : {})}
                    />
                  </li>
                ))}
              </ul>
            )}
            {hiddenChoices > 0 ? (
              <p className="text-xs font-medium text-warning">
                {hiddenChoices === 1
                  ? '1 compromisso escolhido está fora desta busca e continua valendo.'
                  : `${String(hiddenChoices)} compromissos escolhidos estão fora desta busca e continuam valendo.`}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}

/// Quantos pagamentos o diálogo mostra de uma vez. Poucos, porque a lista rola dentro do modal e o
/// rodapé de paginação precisa caber junto com o resumo do que vai ser quitado.
const DIALOG_PAGE_SIZE = 10;

/// Segura o texto até quem digita parar. Sem isto cada tecla viraria uma consulta ao servidor, e a
/// resposta da palavra pela metade poderia chegar depois da palavra inteira.
function useDebounced<T>(value: T, delay = 300): T {
  const [held, setHeld] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setHeld(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return held;
}

/// O espelho: partindo de um compromisso, escolher os pagamentos que o quitaram. É o caso do boleto
/// grande pago em duas vezes.
///
/// O recorte vem pronto do servidor — a direção e a moeda do compromisso, e só o que ainda não foi
/// usado —, com busca e paginação lá também. Antes o diálogo carregava os cinquenta pagamentos
/// pendentes mais antigos e filtrava esses cinquenta: o pagamento que ficasse de fora daquela
/// primeira página não existia para quem procurava.
export function SettleObligationModal({
  target,
  title,
  description,
  outstandingCents,
  currency,
  direction,
  onClose,
  onSettled,
}: {
  target: SettlementTarget;
  title: string;
  description: string;
  outstandingCents: number;
  currency: Currency;
  direction: 'PAYABLE' | 'RECEIVABLE';
  onClose: () => void;
  onSettled: (message: string) => Promise<void>;
}) {
  /// O escolhido guarda a linha inteira, e não o id: ele continua valendo — e continua somando no
  /// rodapé — depois que a busca ou a página seguinte tirarem a linha da vista.
  const [chosen, setChosen] = useState<ReconciliationMovement[]>([]);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string>();
  const term = useDebounced(search.trim());
  const page = useQuery({
    queryKey: ['company', 'reconciliation', 'settleable', direction, currency, term, offset],
    queryFn: () =>
      reconciliationApi.settleable({
        direction: direction === 'PAYABLE' ? 'OUTFLOW' : 'INFLOW',
        currency,
        ...(term ? { search: term } : {}),
        limit: DIALOG_PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const movements = page.data?.movements ?? [];
  const total = page.data?.total ?? 0;
  const chosenIds = new Set(chosen.map((movement) => movement.id));
  const hiddenChoices = chosen.filter(
    (movement) => !movements.some((item) => item.id === movement.id),
  ).length;
  // A gravação distribui do pagamento mais antigo para o mais novo; a prévia segue a mesma ordem,
  // senão prometeria quitar um valor que o servidor reparte de outro jeito.
  const applied = [...chosen]
    .sort((left, right) => left.occurredOn.localeCompare(right.occurredOn))
    .reduce(
      (state, movement) => {
        const amount = Math.min(state.rest, freeOf(movement));
        return { rest: state.rest - amount, used: state.used + amount };
      },
      { rest: outstandingCents, used: 0 },
    );

  function toggle(movement: ReconciliationMovement) {
    setChosen((current) =>
      current.some((item) => item.id === movement.id)
        ? current.filter((item) => item.id !== movement.id)
        : [...current, movement],
    );
  }

  async function submit() {
    setSaving(true);
    setFailure(undefined);
    try {
      await reconciliationApi.settleObligation(
        target,
        chosen.map((movement) => movement.id),
      );
      await onSettled(
        applied.rest > 0
          ? `Conciliado. Ainda faltam ${formatCurrency(applied.rest, currency)} neste compromisso.`
          : 'Compromisso quitado.',
      );
    } catch (error) {
      setFailure(getApiError(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Conciliar compromisso"
      description={`${title} · ${description} · ${formatCurrency(outstandingCents, currency)} em aberto`}
      footer={
        <div className="flex w-full flex-col gap-3">
          <div className="rounded-xl bg-surface-elevated px-3 py-2.5 text-sm">
            <p className="flex justify-between gap-3">
              <span className="text-content-muted">Vai quitar</span>
              <strong className="tabular-nums">{formatCurrency(applied.used, currency)}</strong>
            </p>
            <p className="mt-1 flex justify-between gap-3">
              <span className="text-content-muted">Continua em aberto</span>
              <strong className="tabular-nums">{formatCurrency(applied.rest, currency)}</strong>
            </p>
          </div>
          {failure ? <p className="text-sm font-semibold text-danger-400">{failure}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => void submit()} disabled={chosen.length === 0 || saving}>
              <Check className="size-4" aria-hidden="true" />
              {saving ? 'Conciliando…' : 'Conciliar'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {page.isLoading ? (
          <p className="text-sm text-content-muted">Carregando o extrato…</p>
        ) : page.error ? (
          <RequestAlert message={getApiError(page.error)} />
        ) : total === 0 && term.length === 0 ? (
          <p className="text-sm text-content-muted">
            Nenhum movimento em aberto combina com este compromisso.
          </p>
        ) : (
          <>
            <DialogSearch
              label="Buscar pagamento"
              placeholder="Descrição, conta ou valor"
              value={search}
              // Buscar é recortar tudo de novo, então a primeira página volta a ser a primeira.
              onChange={(value) => {
                setSearch(value);
                setOffset(0);
              }}
              count={countLabel(movements.length, total, chosen.length, 'pagamento')}
            />
            {total === 0 ? (
              <p className="text-sm text-content-muted">Nenhum pagamento encontrado nesta busca.</p>
            ) : (
              <ul className="space-y-2">
                {movements.map((movement) => (
                  <li key={movement.id}>
                    <ChoiceRow
                      checked={chosenIds.has(movement.id)}
                      onToggle={() => toggle(movement)}
                      title={movement.description}
                      subtitle={`${formatCivilDate(movement.occurredOn)} · ${movement.accountName}`}
                      badge={movement.direction === 'OUTFLOW' ? 'Saída' : 'Entrada'}
                      amount={formatCurrency(freeOf(movement), movement.currency)}
                    />
                  </li>
                ))}
              </ul>
            )}
            {total > DIALOG_PAGE_SIZE ? (
              <Pagination
                total={total}
                limit={DIALOG_PAGE_SIZE}
                offset={offset}
                onOffsetChange={setOffset}
                itemLabel={(count) => (count === 1 ? 'pagamento' : 'pagamentos')}
              />
            ) : null}
            {hiddenChoices > 0 ? (
              <p className="text-xs font-medium text-warning">
                {hiddenChoices === 1
                  ? '1 pagamento escolhido está fora desta lista e continua valendo.'
                  : `${String(hiddenChoices)} pagamentos escolhidos estão fora desta lista e continuam valendo.`}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}

function DismissModal({
  entry,
  onClose,
  onDismissed,
}: {
  entry: ReconciliationEntry;
  onClose: () => void;
  onDismissed: (message: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string>();

  async function submit() {
    setSaving(true);
    setFailure(undefined);
    try {
      await reconciliationApi.dismiss(entry.movement.id, reason);
      await onDismissed('Movimento marcado como sem compromisso.');
    } catch (error) {
      setFailure(getApiError(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Movimento sem compromisso"
      description="Tarifa, imposto sobre operação, juros e rendimento não têm conta a pagar nem a receber. A decisão fica registrada e pode ser desfeita."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={reason.trim().length < 3 || saving}>
            {saving ? 'Salvando…' : 'Marcar'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Por que esta linha não terá compromisso?"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Tarifa de pacote mensal"
          maxLength={240}
        />
        {failure ? <p className="text-sm font-semibold text-danger-400">{failure}</p> : null}
      </div>
    </Modal>
  );
}

/// Os três motivos do ADR 0026, na ordem do mais comum para o mais raro. A lista é fechada porque é
/// ela que torna a diferença um número agrupável depois; a observação cobre o que ela não descreve.
const DIFFERENCE_REASONS = [
  {
    value: 'ROUNDING',
    label: 'Arredondamento',
    hint: 'Centavos de diferença entre o que saiu da conta e o que era devido.',
  },
  {
    value: 'BANK_FEE',
    label: 'Tarifa bancária',
    hint: 'O banco cobrou junto, no mesmo lançamento.',
  },
  {
    value: 'CONFIRMED_DIFFERENCE',
    label: 'Diferença confirmada',
    hint: 'Desconto, acréscimo ou valor combinado à parte com a contraparte.',
  },
] as const;

function reasonLabelOf(reason: FinanceDifferenceReason): string {
  return DIFFERENCE_REASONS.find((option) => option.value === reason)?.label ?? 'Diferença';
}

/// Quando a sobra deixa de parecer arredondamento. O sistema não distingue quatro centavos de tarifa
/// de quinhentos reais que ainda não foram conciliados — as duas são sobra. Quem distingue é a
/// pessoa, e o aviso existe para ela reparar antes de escolher um motivo (ADR 0026).
function looksBig(residualCents: number, amountCents: number): boolean {
  return residualCents > Math.max(100, Math.round(amountCents * 0.01));
}

/// O que sobrou deste pagamento depois do que ele quitou, explicado por uma pessoa (ADR 0026).
///
/// O valor não é digitado nem enviado: quem o aponta é o servidor, sobre os vínculos vivos. A tela
/// mostra o que apurou apenas para a pessoa conferir antes de assinar embaixo.
function ResolveDifferenceModal({
  entry,
  onClose,
  onResolved,
}: {
  entry: ReconciliationEntry;
  onClose: () => void;
  onResolved: (message: string) => Promise<void>;
}) {
  const residual = availableOf(entry);
  const [reason, setReason] = useState<FinanceDifferenceReason>('ROUNDING');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string>();

  async function submit() {
    setSaving(true);
    setFailure(undefined);
    try {
      const result = await reconciliationApi.resolveDifference(
        entry.movement.id,
        reason,
        note.trim() === '' ? null : note.trim(),
      );
      await onResolved(
        `Diferença de ${formatCurrency(result.amountCents, entry.movement.currency)} resolvida.`,
      );
    } catch (error) {
      setFailure(getApiError(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Resolver diferença"
      description={`${entry.movement.description} · sobram ${formatCurrency(residual, entry.movement.currency)} depois do que este pagamento quitou`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? 'Resolvendo…' : 'Resolver'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {looksBig(residual, entry.movement.amountCents) ? (
          <p className="rounded-xl bg-surface-elevated px-3 py-2 text-sm font-medium text-warning">
            Esta sobra é grande perto do valor do pagamento. Confira se não falta conciliar um
            compromisso antes de declará-la resolvida.
          </p>
        ) : null}
        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-content">O que é esta sobra?</legend>
          <div role="radiogroup" className="space-y-2">
            {DIFFERENCE_REASONS.map((option) => (
              <ReasonOption
                key={option.value}
                checked={reason === option.value}
                onChoose={() => setReason(option.value)}
                label={option.label}
                hint={option.hint}
              />
            ))}
          </div>
        </fieldset>
        <Input
          label="Observação (opcional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Tarifa de TED cobrada no mesmo débito"
          maxLength={240}
        />
        {failure ? <p className="text-sm font-semibold text-danger-400">{failure}</p> : null}
      </div>
    </Modal>
  );
}

/// Escolha única no padrão das listas deste diálogo. O rádio nativo não tem o mesmo estado visual, e
/// misturar os dois deixaria a mesma decisão com duas aparências na mesma tela.
function ReasonOption({
  checked,
  onChoose,
  label,
  hint,
}: {
  checked: boolean;
  onChoose: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onChoose}
      className={`flex w-full cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
        checked
          ? 'border-brand-500 bg-brand-500/10'
          : 'border-surface-border bg-surface-elevated hover:border-brand-500/40'
      }`}
    >
      <span
        aria-hidden="true"
        className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border ${
          checked ? 'border-brand-500 bg-brand-500' : 'border-surface-border'
        }`}
      >
        {checked ? <span className="size-2 rounded-full bg-on-brand" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-content">{label}</span>
        <span className="mt-0.5 block break-words text-xs text-content-muted">{hint}</span>
      </span>
    </button>
  );
}

function ChoiceRow({
  checked,
  onToggle,
  title,
  subtitle,
  badge,
  amount,
  note,
}: {
  checked: boolean;
  onToggle: () => void;
  title: string;
  subtitle: string;
  badge: string;
  amount: string;
  /// Aviso da linha, quando ela precisa de um. Hoje só a obrigação já baixada à mão tem um: quem
  /// escolhe precisa saber que conciliar vai substituir aquela baixa (ADR 0025).
  note?: string | undefined;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      className={`flex w-full cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
        checked
          ? 'border-brand-500 bg-brand-500/10'
          : 'border-surface-border bg-surface-elevated hover:border-brand-500/40'
      }`}
    >
      <span
        aria-hidden="true"
        className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-md border ${
          checked ? 'border-brand-500 bg-brand-500 text-on-brand' : 'border-surface-border'
        }`}
      >
        {checked ? <Check className="size-3.5" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block break-words font-semibold text-content">{title}</span>
        <span className="mt-0.5 block break-words text-xs text-content-muted">{subtitle}</span>
        <span className="mt-1 inline-block text-xs text-content-faint">{badge}</span>
        {note ? (
          <span className="mt-1 block break-words text-xs font-medium text-warning">{note}</span>
        ) : null}
      </span>
      <span className="shrink-0 text-sm font-semibold tabular-nums">{amount}</span>
    </button>
  );
}
